import type { Attachment, ChannelEvent, Message, Reaction } from '../api/types'
import type { LocalChannel, MemberRow } from '../db/db'
import { lastMessageMedia, withAttachmentDefaults } from '../lib/media'
import { canPostIn } from '../lib/posting'

/** The slice of local state an event can touch. */
export interface ChannelState {
  channel: LocalChannel
  members: MemberRow[]
  /** The message referenced by the event, when it exists locally. */
  message: Message | undefined
}

export interface ReduceContext {
  meId: string
}

export type ReduceResult =
  /** event.seq <= headSeq: already applied. */
  | { status: 'duplicate' }
  /** event.seq > headSeq + 1: a sync is needed. */
  | { status: 'gap' }
  /** The current user was removed from the channel: delete it locally. */
  | { status: 'removed' }
  | {
      status: 'applied'
      channel: LocalChannel
      members: MemberRow[]
      /** Present when the message must be written (created or patched). */
      message: Message | undefined
    }

/** Id of the message an event refers to, used to load it before reducing. */
export function targetMessageId(event: ChannelEvent): string | undefined {
  switch (event.type) {
    case 'message.created':
      return event.payload.message.id
    case 'message.edited':
    case 'message.deleted':
    case 'reaction.added':
    case 'reaction.removed':
    case 'message.pinned':
    case 'message.unpinned':
    case 'message.options_closed':
    case 'checklist.ticked':
    case 'checklist.unticked':
    case 'attachment.ready':
      return event.payload.messageId
    default:
      return undefined
  }
}

export function classifyEvent(headSeq: number, eventSeq: number): 'apply' | 'duplicate' | 'gap' {
  if (eventSeq <= headSeq) return 'duplicate'
  if (eventSeq === headSeq + 1) return 'apply'
  return 'gap'
}

function addReaction(reactions: Reaction[], emoji: string, userId: string): Reaction[] {
  const existing = reactions.find((r) => r.emoji === emoji)
  if (!existing) return [...reactions, { emoji, userIds: [userId] }]
  if (existing.userIds.includes(userId)) return reactions
  return reactions.map((r) => (r.emoji === emoji ? { ...r, userIds: [...r.userIds, userId] } : r))
}

function removeReaction(reactions: Reaction[], emoji: string, userId: string): Reaction[] {
  return reactions
    .map((r) => (r.emoji === emoji ? { ...r, userIds: r.userIds.filter((id) => id !== userId) } : r))
    .filter((r) => r.userIds.length > 0)
}

function patchAttachment(attachments: Attachment[], attachment: Attachment): Attachment[] {
  if (!attachments.some((a) => a.id === attachment.id)) return [...attachments, withAttachmentDefaults(attachment)]
  return attachments.map((a) => (a.id === attachment.id ? withAttachmentDefaults({ ...a, ...attachment }) : a))
}

function unique(ids: string[]): string[] {
  return [...new Set(ids)]
}

/** A question with the answer that just arrived, unless it already has one (the first answer is the one that counts). */
export function markAnswered(question: Message, answer: Message): Message {
  if (question.answered || !answer.answer) return question
  return { ...question, answered: { optionId: answer.answer.optionId, messageId: answer.id, answeredAt: answer.createdAt } }
}

/**
 * Pure application of one channel event (contract: "Client application rule").
 * Never mutates its inputs.
 */
export function reduceEvent(state: ChannelState, event: ChannelEvent, ctx: ReduceContext): ReduceResult {
  const kind = classifyEvent(state.channel.headSeq, event.seq)
  if (kind === 'duplicate') return { status: 'duplicate' }
  if (kind === 'gap') return { status: 'gap' }

  let channel: LocalChannel = { ...state.channel, headSeq: event.seq, version: event.version }
  let members = state.members
  let message: Message | undefined
  const existing = state.message

  switch (event.type) {
    case 'message.created': {
      const created = event.payload.message
      // Attachments sent before video and audio existed lack the media fields.
      message = { ...created, channelId: created.channelId || channel.id, attachments: created.attachments.map(withAttachmentDefaults) }
      const isNew = !existing
      channel.lastMessage = {
        id: created.id,
        authorId: created.authorId,
        body: created.body,
        createdAt: created.createdAt,
        hasAttachments: created.attachments.length > 0,
        media: lastMessageMedia(message.attachments),
      }
      if (isNew && created.authorId !== ctx.meId && !created.deletedAt && created.seq > channel.lastReadSeq) {
        channel.unread = channel.unread + 1
      }
      break
    }

    case 'message.edited': {
      const { messageId, body, editedAt } = event.payload
      if (existing) message = { ...existing, body, editedAt }
      if (channel.lastMessage?.id === messageId) channel.lastMessage = { ...channel.lastMessage, body }
      break
    }

    case 'message.deleted': {
      const { messageId, deletedAt } = event.payload
      if (existing) {
        // A deleted forward is no longer marked as one (contract: Message.forwarded).
        message = { ...existing, body: '', deletedAt, attachments: [], reactions: [], forwarded: null, options: null, ticks: null, checklist: null }
        const wasUnread =
          !existing.deletedAt && existing.authorId !== ctx.meId && existing.seq > channel.lastReadSeq
        if (wasUnread) channel.unread = Math.max(0, channel.unread - 1)
      }
      if (channel.lastMessage?.id === messageId) {
        channel.lastMessage = { ...channel.lastMessage, body: '', hasAttachments: false, media: null }
      }
      if (channel.pinnedMessageIds.includes(messageId)) {
        channel.pinnedMessageIds = channel.pinnedMessageIds.filter((id) => id !== messageId)
      }
      break
    }

    case 'reaction.added': {
      const { emoji, userId } = event.payload
      if (existing) message = { ...existing, reactions: addReaction(existing.reactions, emoji, userId) }
      break
    }

    case 'reaction.removed': {
      const { emoji, userId } = event.payload
      if (existing) message = { ...existing, reactions: removeReaction(existing.reactions, emoji, userId) }
      break
    }

    case 'message.options_closed': {
      // Not an edit: the words and editedAt stay as they are.
      if (existing) message = { ...existing, optionsClosedAt: event.payload.closedAt }
      break
    }

    case 'checklist.ticked':
    case 'checklist.unticked': {
      const { itemId } = event.payload
      if (existing) {
        const others = (existing.ticks ?? []).filter((id) => id !== itemId)
        message = { ...existing, ticks: event.type === 'checklist.ticked' ? [...others, itemId] : others }
      }
      break
    }

    case 'member.added': {
      const added = event.payload.members
      const byId = new Map(members.map((m) => [m.userId, m]))
      // Upsert: an integration converting a manual member to managed arrives as member.added for an existing member.
      for (const m of added) {
        byId.set(m.userId, { channelId: channel.id, userId: m.userId, role: m.role, source: m.source ?? 'manual' })
      }
      const before = new Set([...members.map((m) => m.userId), ...channel.memberIds])
      members = [...byId.values()]
      channel.memberCount = (channel.memberCount ?? before.size) + unique(added.map((m) => m.userId)).filter((id) => !before.has(id)).length
      // A channel's summary lists no members (its rows are the list); small conversations keep theirs in the summary.
      if (channel.type !== 'channel') channel.memberIds = unique([...channel.memberIds, ...added.map((m) => m.userId)])
      const mine = added.find((m) => m.userId === ctx.meId)
      if (mine) {
        channel.isMember = true
        channel.myRole = mine.role
      }
      break
    }

    case 'member.removed': {
      const { userId } = event.payload
      if (userId === ctx.meId && state.channel.isMember) return { status: 'removed' }
      if (members.some((m) => m.userId === userId) || channel.memberIds.includes(userId)) channel.memberCount = Math.max(0, (channel.memberCount ?? members.length) - 1)
      members = members.filter((m) => m.userId !== userId)
      channel.memberIds = channel.memberIds.filter((id) => id !== userId)
      break
    }

    case 'member.role_changed': {
      const { userId, role } = event.payload
      members = members.map((m) => (m.userId === userId ? { ...m, role } : m))
      if (userId === ctx.meId) channel.myRole = role
      break
    }

    case 'channel.updated': {
      const payload = event.payload
      if ('name' in payload) channel.name = payload.name
      if ('topic' in payload) channel.topic = payload.topic
      // Events recorded before restricted channels existed have no `posting` or `replyBot`: keep the current values.
      if (payload.posting) channel.posting = payload.posting
      if ('replyBot' in payload) channel.replyBot = payload.replyBot ?? null
      // Events recorded before managed channels existed have no `managed`: keep the current value.
      if ('managed' in payload) {
        channel.managed = payload.managed ?? null
        // Released by the integration: every member becomes a manual member (contract: admin release).
        if (!channel.managed && members.some((m) => m.source === 'managed')) {
          members = members.map((m) => (m.source === 'managed' ? { ...m, source: 'manual' } : m))
        }
      }
      break
    }

    case 'channel.archived':
      channel.archivedAt = event.payload.archivedAt
      break

    case 'channel.unarchived':
      channel.archivedAt = null
      break

    case 'message.pinned': {
      const { messageId } = event.payload
      if (!channel.pinnedMessageIds.includes(messageId)) {
        channel.pinnedMessageIds = [...channel.pinnedMessageIds, messageId]
      }
      break
    }

    case 'message.unpinned': {
      const { messageId } = event.payload
      channel.pinnedMessageIds = channel.pinnedMessageIds.filter((id) => id !== messageId)
      break
    }

    case 'attachment.ready': {
      const { messageId, attachment } = event.payload
      if (existing) {
        message = { ...existing, attachments: patchAttachment(existing.attachments, attachment) }
        // A converted clip's length is measured: the conversation list says it from now on.
        if (channel.lastMessage?.id === messageId && !existing.deletedAt) {
          channel.lastMessage = { ...channel.lastMessage, media: lastMessageMedia(message.attachments) }
        }
      }
      break
    }

    default: {
      // Unknown event type from a newer server: advance the head so the chain stays consistent.
      const _exhaustive: never = event
      void _exhaustive
    }
  }

  // Posting depends on membership, role, archive and the posting setting: keep the stored flag in step.
  channel = { ...channel, canPost: canPostIn(channel) }
  return { status: 'applied', channel, members, message }
}
